import { useMemo, useState } from 'react';

export interface SortState<K extends string> {
  key: K;
  dir: 'asc' | 'desc';
}

export function useSort<T, K extends string>(
  rows: T[],
  initial: SortState<K>,
  accessor: (row: T, key: K) => number | string | null
) {
  const [sort, setSort] = useState<SortState<K>>(initial);
  const sorted = useMemo(() => {
    const val = (row: T) => {
      const v = accessor(row, sort.key);
      return v === null ? -Infinity : v;
    };
    return [...rows].sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      const c = typeof va === 'string' && typeof vb === 'string' ? va.localeCompare(vb) : Number(va) - Number(vb);
      return sort.dir === 'asc' ? c : -c;
    });
    // accessor is a stable mapping by convention
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort]);
  const onSort = (key: K) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));
  return { sorted, sort, onSort };
}
